import {
  MathUtils,
  Object3D,
  Quaternion,
  Vector2,
  Vector3
} from "three";

type VectorConfig = {
  x: number;
  y: number;
  z: number;
};

export type WeaponIkDriver = {
  isReady: boolean;
  setPointerTarget: (pointer: Vector2) => void;
  resetRestPose: () => void;
  update: () => void;
  getEffectorWorldPosition: (target: Vector3) => Vector3;
};

type IkSolveOrder = "effector-first" | "base-first";

type WeaponIkConfig = {
  root: Object3D;
  targetSpace?: Object3D;
  chain: readonly string[];
  effector: string;
  horizontal: VectorConfig;
  vertical: VectorConfig;
  bias?: VectorConfig;
  iterations?: number;
  maxAngle?: number;
  stopDistance?: number;
  bendPlaneNormal?: VectorConfig;
  minReachRatio?: number;
  maxReachRatio?: number;
  maxJointSwing?: number;
  jointSwingOverrides?: Record<string, number>;
  lockedTerminalLinks?: number;
  bendPlaneSpace?: Object3D;
  solveOrder?: IkSolveOrder;
};

const ZERO_VECTOR: VectorConfig = { x: 0, y: 0, z: 0 };
const MIN_DIRECTION_LENGTH_SQ = 0.000001;

const getBoneNameVariants = (name: string) => {
  const variants = [name];
  const compactMatch = /^Bone(\d+)$/.exec(name);
  const dotMatch = /^Bone\.(\d+)$/.exec(name);

  if (compactMatch) {
    variants.push(`Bone.${compactMatch[1].padStart(3, "0")}`);
  }

  if (dotMatch) {
    variants.push(`Bone${dotMatch[1].padStart(3, "0")}`);
  }

  return variants;
};

const findObjectByFlexibleName = (root: Object3D, name: string) => {
  for (const variant of getBoneNameVariants(name)) {
    const object = root.getObjectByName(variant);

    if (object) return object;
  }

  return null;
};

const addScaledVectorConfig = (
  target: Vector3,
  vector: VectorConfig,
  scale: number
) => {
  target.x += vector.x * scale;
  target.y += vector.y * scale;
  target.z += vector.z * scale;
};

const vectorFromConfig = (vector: VectorConfig) =>
  new Vector3(vector.x, vector.y, vector.z);

export const createWeaponIkDriver = ({
  root,
  targetSpace: configuredTargetSpace,
  chain,
  effector: effectorName,
  horizontal,
  vertical,
  bias = ZERO_VECTOR,
  iterations = 6,
  maxAngle = 0.16,
  stopDistance = 0.01,
  bendPlaneNormal,
  minReachRatio,
  maxReachRatio,
  maxJointSwing,
  jointSwingOverrides,
  lockedTerminalLinks = 0,
  bendPlaneSpace: configuredBendPlaneSpace,
  solveOrder = "effector-first"
}: WeaponIkConfig): WeaponIkDriver => {
  const targetSpace = configuredTargetSpace ?? root;
  const bendPlaneSpace = configuredBendPlaneSpace ?? targetSpace;
  const chainBones = chain
    .map((name) => findObjectByFlexibleName(root, name))
    .filter((bone): bone is Object3D => Boolean(bone));
  const effector = findObjectByFlexibleName(root, effectorName);
  const effectorIndex = chainBones.findIndex((bone) => bone === effector);
  const allLinks =
    effectorIndex > 0 ? chainBones.slice(0, effectorIndex).reverse() : [];
  const lockedTerminalLinkCount = MathUtils.clamp(
    Math.floor(lockedTerminalLinks),
    0,
    allLinks.length
  );
  const links = allLinks.slice(lockedTerminalLinkCount);
  const solverLinkIndexes = links.map((_, index) =>
    solveOrder === "base-first" ? links.length - 1 - index : index
  );

  const targetLocalPosition = new Vector3();
  const restEffectorLocalPosition = new Vector3();
  const restEffectorWorldPosition = new Vector3();
  const targetWorldPosition = new Vector3();
  const effectorWorldPosition = new Vector3();
  const baseWorldPosition = new Vector3();
  const previousChainWorldPosition = new Vector3();
  const nextChainWorldPosition = new Vector3();
  const targetOffset = new Vector3();
  const restBaseToEffectorDirection = new Vector3();
  const linkWorldPosition = new Vector3();
  const linkWorldQuaternion = new Quaternion();
  const inverseLinkWorldQuaternion = new Quaternion();
  const bendPlaneSpaceWorldQuaternion = new Quaternion();
  const effectorDirection = new Vector3();
  const targetDirection = new Vector3();
  const rotationAxis = new Vector3();
  const rotationDelta = new Quaternion();
  const limitedQuaternion = new Quaternion();
  const bendPlaneNormalWorld = new Vector3();
  const bendAxis = new Vector3();
  const lastPointer = new Vector2();
  const restLinkQuaternions = links.map((link) => link.quaternion.clone());

  let configuredBendPlaneNormal = bendPlaneNormal
    ? vectorFromConfig(bendPlaneNormal)
    : null;

  if (!effector || links.length === 0) {
    return {
      isReady: false,
      setPointerTarget: () => {},
      resetRestPose: () => {},
      update: () => {},
      getEffectorWorldPosition: (target) => target.set(0, 0, 0)
    };
  }

  let chainReach = 0;
  let minReach = 0;
  let maxReach = Number.POSITIVE_INFINITY;

  if (
    configuredBendPlaneNormal &&
    configuredBendPlaneNormal.lengthSq() < MIN_DIRECTION_LENGTH_SQ
  ) {
    configuredBendPlaneNormal = null;
  } else {
    configuredBendPlaneNormal?.normalize();
  }

  const jointSwingLimit =
    maxJointSwing !== undefined && maxJointSwing > 0 ? maxJointSwing : null;

  const getMappedNumber = (
    values: Record<string, number> | undefined,
    name: string
  ) => {
    if (!values) return undefined;

    for (const variant of getBoneNameVariants(name)) {
      const value = values[variant];

      if (value !== undefined) {
        return value;
      }
    }

    return undefined;
  };

  const getJointSwingLimit = (link: Object3D) => {
    const override = getMappedNumber(jointSwingOverrides, link.name);

    if (override !== undefined && override > 0) {
      return override;
    }

    return jointSwingLimit;
  };

  const applyPointerTarget = () => {
    targetLocalPosition.copy(restEffectorLocalPosition);
    addScaledVectorConfig(targetLocalPosition, horizontal, lastPointer.x);
    addScaledVectorConfig(targetLocalPosition, vertical, lastPointer.y);
    addScaledVectorConfig(targetLocalPosition, bias, 1);
  };

  const resetRestPose = () => {
    targetSpace.updateMatrixWorld(true);
    root.updateMatrixWorld(true);
    effector.getWorldPosition(restEffectorWorldPosition);
    restEffectorLocalPosition.copy(restEffectorWorldPosition);
    targetSpace.worldToLocal(restEffectorLocalPosition);

    chainBones[0].getWorldPosition(baseWorldPosition);
    restBaseToEffectorDirection.subVectors(
      restEffectorWorldPosition,
      baseWorldPosition
    );

    if (restBaseToEffectorDirection.lengthSq() < MIN_DIRECTION_LENGTH_SQ) {
      restBaseToEffectorDirection.set(0, -1, 0);
    } else {
      restBaseToEffectorDirection.normalize();
    }

    chainReach = 0;
    chainBones[0].getWorldPosition(previousChainWorldPosition);

    for (let index = 1; index < chainBones.length; index += 1) {
      chainBones[index].getWorldPosition(nextChainWorldPosition);
      chainReach += previousChainWorldPosition.distanceTo(nextChainWorldPosition);
      previousChainWorldPosition.copy(nextChainWorldPosition);
    }

    minReach =
      chainReach > 0 && minReachRatio !== undefined
        ? chainReach * MathUtils.clamp(minReachRatio, 0, 1)
        : 0;
    maxReach =
      chainReach > 0 && maxReachRatio !== undefined
        ? chainReach * Math.max(maxReachRatio, minReachRatio ?? 0)
        : Number.POSITIVE_INFINITY;

    links.forEach((link, index) => {
      restLinkQuaternions[index].copy(link.quaternion);
    });

    applyPointerTarget();
  };

  const getTargetWorldPosition = () => {
    return targetSpace.localToWorld(targetWorldPosition.copy(targetLocalPosition));
  };

  const clampTargetReach = () => {
    if (chainReach <= 0) return;

    chainBones[0].getWorldPosition(baseWorldPosition);
    targetOffset.subVectors(targetWorldPosition, baseWorldPosition);

    const distance = targetOffset.length();

    if (distance < MIN_DIRECTION_LENGTH_SQ) {
      targetOffset.copy(restBaseToEffectorDirection);
    } else {
      targetOffset.divideScalar(distance);
    }

    const clampedDistance = MathUtils.clamp(distance, minReach, maxReach);

    if (Math.abs(clampedDistance - distance) < 0.0001) return;

    targetWorldPosition
      .copy(baseWorldPosition)
      .addScaledVector(targetOffset, clampedDistance);
  };

  const clampLinkSwing = (link: Object3D, index: number) => {
    const linkSwingLimit = getJointSwingLimit(link);

    if (linkSwingLimit === null) return;

    const restQuaternion = restLinkQuaternions[index];
    const angle = restQuaternion.angleTo(link.quaternion);

    if (angle <= linkSwingLimit) return;

    limitedQuaternion.copy(link.quaternion);
    link.quaternion
      .copy(restQuaternion)
      .slerp(limitedQuaternion, linkSwingLimit / angle);
  };

  const clampAllLinkSwing = () => {
    links.forEach((link, index) => {
      clampLinkSwing(link, index);
      link.updateMatrixWorld(true);
    });
  };

  resetRestPose();

  return {
    isReady: true,
    setPointerTarget: (pointer) => {
      lastPointer.copy(pointer);
      applyPointerTarget();
    },
    resetRestPose,
    update: () => {
      targetSpace.updateMatrixWorld(true);
      root.updateMatrixWorld(true);
      getTargetWorldPosition();
      clampTargetReach();

      if (configuredBendPlaneNormal) {
        bendPlaneSpace.updateMatrixWorld(true);
        bendPlaneSpace.getWorldQuaternion(bendPlaneSpaceWorldQuaternion);
        bendPlaneNormalWorld
          .copy(configuredBendPlaneNormal)
          .applyQuaternion(bendPlaneSpaceWorldQuaternion)
          .normalize();
      }

      clampAllLinkSwing();

      for (let iteration = 0; iteration < iterations; iteration += 1) {
        effector.getWorldPosition(effectorWorldPosition);

        if (
          effectorWorldPosition.distanceToSquared(targetWorldPosition) <=
          stopDistance * stopDistance
        ) {
          break;
        }

        for (const linkIndex of solverLinkIndexes) {
          const link = links[linkIndex];

          link.getWorldPosition(linkWorldPosition);
          link.getWorldQuaternion(linkWorldQuaternion);
          inverseLinkWorldQuaternion.copy(linkWorldQuaternion).invert();

          effector.getWorldPosition(effectorWorldPosition);
          effectorDirection
            .subVectors(effectorWorldPosition, linkWorldPosition)
            .applyQuaternion(inverseLinkWorldQuaternion);
          targetDirection
            .subVectors(targetWorldPosition, linkWorldPosition)
            .applyQuaternion(inverseLinkWorldQuaternion);

          if (
            effectorDirection.lengthSq() < MIN_DIRECTION_LENGTH_SQ ||
            targetDirection.lengthSq() < MIN_DIRECTION_LENGTH_SQ
          ) {
            continue;
          }

          if (configuredBendPlaneNormal) {
            bendAxis
              .copy(bendPlaneNormalWorld)
              .applyQuaternion(inverseLinkWorldQuaternion);

            if (bendAxis.lengthSq() < MIN_DIRECTION_LENGTH_SQ) continue;

            bendAxis.normalize();
            effectorDirection.addScaledVector(
              bendAxis,
              -effectorDirection.dot(bendAxis)
            );
            targetDirection.addScaledVector(
              bendAxis,
              -targetDirection.dot(bendAxis)
            );

            if (
              effectorDirection.lengthSq() < MIN_DIRECTION_LENGTH_SQ ||
              targetDirection.lengthSq() < MIN_DIRECTION_LENGTH_SQ
            ) {
              continue;
            }
          }

          effectorDirection.normalize();
          targetDirection.normalize();

          const dot = MathUtils.clamp(
            effectorDirection.dot(targetDirection),
            -1,
            1
          );
          const angle = Math.acos(dot);

          if (angle < 0.0001) continue;

          if (configuredBendPlaneNormal) {
            rotationAxis.crossVectors(effectorDirection, targetDirection);
            rotationDelta.setFromAxisAngle(
              bendAxis,
              MathUtils.clamp(
                Math.atan2(rotationAxis.dot(bendAxis), dot),
                -maxAngle,
                maxAngle
              )
            );
          } else {
            rotationAxis.crossVectors(effectorDirection, targetDirection);

            if (rotationAxis.lengthSq() < MIN_DIRECTION_LENGTH_SQ) continue;

            rotationAxis.normalize();
            rotationDelta.setFromAxisAngle(
              rotationAxis,
              Math.min(angle, maxAngle)
            );
          }
          link.quaternion.multiply(rotationDelta);
          clampLinkSwing(link, linkIndex);
          link.updateMatrixWorld(true);
        }
      }
    },
    getEffectorWorldPosition: (target) => {
      root.updateMatrixWorld(true);
      effector.getWorldPosition(target);

      return target;
    }
  };
};
